import type { ComponentType } from 'react';
import { AreaDetailPage } from '@/components/AreaDetailPage';
import { generateAllAreaConfigs } from '@/lib/areaPageData';
import heavyLifting from '../../images/VIP House Movers Team Heavy Lifting.jpg';
import removalWithFleet from '../../images/VIP House Movers Removal Team with Fleet.png';
import homeRemovalVan from '../../images/VIP House Movers Home Removal Van.jpg';

export type AreaPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  movingSupport: string[];
  idealFor: string[];
  seoPoints: string[];
  faqs: Array<{ question: string; answer: string }>;
  backgroundImage?: string;
};

const FEATURED_AREA_IMAGES = [heavyLifting, removalWithFleet, homeRemovalVan];

function pickFeaturedImage(path: string) {
  let hash = 0;
  for (let i = 0; i < path.length; i += 1) hash = (hash * 31 + path.charCodeAt(i)) >>> 0;
  return FEATURED_AREA_IMAGES[hash % FEATURED_AREA_IMAGES.length];
}

function createAreaPage(config: AreaPageConfig): ComponentType {
  return function AreaPage() {
    return (
      <AreaDetailPage
        activePath={config.path}
        areaLabel={config.navLabel}
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        movingSupport={config.movingSupport}
        idealFor={config.idealFor}
        seoPoints={config.seoPoints}
        faqs={config.faqs}
        backgroundImage={config.backgroundImage ?? pickFeaturedImage(config.path)}
      />
    );
  };
}

const manualAreaPageConfigs: AreaPageConfig[] = [
  {
    path: '/areas-we-cover/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Staffordshire Removals',
    title: 'Removals in Staffordshire by VIP House Movers',
    description: 'VIP House Movers provides dependable house removals, business relocations, packing support, and tailored moving services across Staffordshire.',
    intro: 'Whether you are moving within Staffordshire or relocating into the county, VIP House Movers delivers organised planning, careful handling, and a premium moving experience designed to reduce stress from start to finish.',
    highlights: [
      'Local and long-distance removals throughout Staffordshire',
      'House moves, office relocations, and tailored packing support',
      'Flexible booking options for planned and time-sensitive moves',
      'Professional movers focused on care, communication, and efficiency',
    ],
    movingSupport: [
      'Pre-move planning around access, parking, property size, and timelines.',
      'Careful loading and transport for furniture, boxes, fragile belongings, and business equipment.',
      'Support for part-load moves, full property relocations, and specialist items.',
      'Clear communication from quote to delivery so your Staffordshire move feels fully managed.',
    ],
    idealFor: [
      'Families moving between towns and villages in Staffordshire',
      'Businesses relocating premises with minimal disruption',
      'Customers who want a premium removals company with a personal approach',
    ],
    seoPoints: [
      'Ideal for house removals across Staffordshire towns, villages, and commuter locations.',
      'Suitable for customers moving locally, regionally, or into Staffordshire from elsewhere in the UK.',
      'Popular with families needing packing, loading, transport, and furniture support in one service.',
      'A dependable option for businesses and households wanting a fully insured removals team.',
    ],
    faqs: [
      {
        question: 'Do you cover both local and long-distance moves in Staffordshire?',
        answer: 'Yes. VIP House Movers supports local Staffordshire moves as well as relocations into and out of the county, depending on your destination and service needs.',
      },
      {
        question: 'Can I book packing help for a Staffordshire move?',
        answer: 'Yes. Packing support can be arranged alongside your removal service if you want extra help protecting and organising your belongings before moving day.',
      },
    ],
  },
  {
    path: '/areas-we-cover/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Wolverhampton Removals',
    title: 'Wolverhampton House Removals | VIP House Movers',
    description: 'Wolverhampton house removals and local moving services from VIP House Movers. Fully insured, expert home movers handling flats, houses, businesses, and specialist relocations across Wolverhampton.',
    intro: 'VIP House Movers is your trusted Wolverhampton house removals company. We know the city, the traffic patterns, and the property access challenges so your move is planned around what Wolverhampton customers need most.',
    highlights: [
      'Wolverhampton house removals with local access and property knowledge',
      'Trusted movers for flats, homes, offices, and specialist relocation jobs',
      'City-savvy planning for parking, lift access, and narrow roads',
      'Clear communication, flexible scheduling, and professional handling',
    ],
    movingSupport: [
      'Move planning that considers Wolverhampton streets, parking restrictions, and property access.',
      'Careful handling for furniture, appliances, boxed items, and fragile belongings.',
      'Support for same-day moves, pre-booked home removals, and regional relocations.',
      'A premium removal experience built around local Wolverhampton customers and property types.',
    ],
    idealFor: [
      'Homeowners and tenants moving within Wolverhampton',
      'Families relocating to or from Wolverhampton homes and flats',
      'Businesses moving offices, retail spaces, and local commercial premises',
    ],
    seoPoints: [
      'Perfect for Wolverhampton house removals, flat moves, and local office relocations.',
      'Exact-match local content for removals in Wolverhampton and home moving support.',
      'Designed for customers searching for the best Wolverhampton removals company.',
      'Trusted by local customers looking for a fully insured, professional moving team.',
    ],
    faqs: [
      {
        question: 'Are you a local Wolverhampton removals company?',
        answer: 'Yes. VIP House Movers is based in Wolverhampton and supports customers across the city and surrounding areas with local knowledge and premium moving support.',
      },
      {
        question: 'Do you handle both home and business moves in Wolverhampton?',
        answer: 'Yes. We help with house removals, flat moves, office relocations, packing services, and specialist moving requirements across Wolverhampton.',
      },
      {
        question: 'Can you manage house removals in Wolverhampton flats and terraced homes?',
        answer: 'Yes. Our Wolverhampton house removals team is experienced with flats, terraced homes, semi-detached houses, and apartments, and we plan every move around access, stairs, and parking conditions.',
      },
    ],
  },
  {
    path: '/areas-we-cover/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Birmingham Removals',
    title: 'Removals in Birmingham by VIP House Movers',
    description: 'VIP House Movers delivers organised and professional removals in Birmingham for households, apartment residents, and businesses needing dependable support.',
    intro: 'Moving in Birmingham often involves busy streets, apartment access, loading restrictions, and tight schedules, so VIP House Movers plans every relocation carefully to keep the process smooth and stress-free.',
    highlights: [
      'Professional removals for homes, apartments, and offices across Birmingham',
      'Support for packing, loading, transport, and furniture reassembly',
      'Planning around city access, parking, and timing restrictions',
      'Reliable service with the premium VIP House Movers standard',
    ],
    movingSupport: [
      'Tailored move planning based on your property type, location, and access needs.',
      'Efficient loading and secure transport for local and long-distance Birmingham moves.',
      'Support for students, families, professionals, and growing businesses.',
      'A calm, organised service that keeps moving day under control in busy city settings.',
    ],
    idealFor: [
      'Customers moving within Birmingham or into the city',
      'Apartment residents with lift, concierge, or loading bay requirements',
      'Businesses wanting a structured commercial move',
    ],
    seoPoints: [
      'Strong fit for Birmingham apartment moves, city centre relocations, and family home removals.',
      'Useful when parking restrictions, concierge access, or loading bay rules need planning.',
      'Suitable for students, professionals, growing families, and commercial office moves.',
      'Helps customers navigate busier urban move days with more structure and support.',
    ],
    faqs: [
      {
        question: 'Can you manage removals in busy parts of Birmingham?',
        answer: 'Yes. VIP House Movers plans around city access, parking restrictions, apartment building rules, and timing constraints to keep Birmingham moves more controlled.',
      },
      {
        question: 'Do you handle apartment and office moves in Birmingham?',
        answer: 'Yes. We support apartment relocations, family home moves, and business removals across Birmingham with service tailored to the property type.',
      },
    ],
  },
  {
    path: '/areas-we-cover/walsall',
    navLabel: 'Walsall',
    cardTitle: 'Walsall Removals',
    title: 'Removals in Walsall by VIP House Movers',
    description: 'VIP House Movers offers reliable removals in Walsall with tailored support for family homes, rented properties, offices, and urgent relocations.',
    intro: 'From straightforward house moves to more complex relocations with stairs, parking limits, or delicate belongings, VIP House Movers helps Walsall customers move with confidence and less stress.',
    highlights: [
      'House removals, office relocations, and packing support in Walsall',
      'Careful handling of large furniture, boxed items, and fragile belongings',
      'Flexible service levels to match your budget and timeline',
      'Professional movers with a strong focus on service and presentation',
    ],
    movingSupport: [
      'Planning around property access, moving dates, and transport requirements.',
      'Support for local moves, same-day jobs, and relocations beyond Walsall.',
      'Practical solutions for flats, houses, offices, and part-load transport.',
      'A premium removals experience designed to make moving day easier.',
    ],
    idealFor: [
      'Families moving between neighbourhoods in Walsall',
      'Landlords, tenants, and homeowners preparing for a property move',
      'Businesses needing careful and efficient relocation support',
    ],
    seoPoints: [
      'Ideal for Walsall households planning local moves, downsizing, or moving into a first home.',
      'Useful for landlords and tenants needing practical support with timed property changes.',
      'Suitable for office furniture transport and smaller commercial relocations in the area.',
      'A dependable option for customers wanting flexible support and careful handling.',
    ],
    faqs: [
      {
        question: 'Do you support local house removals in Walsall?',
        answer: 'Yes. VIP House Movers helps customers move between Walsall neighbourhoods and can also support relocations to destinations further afield.',
      },
      {
        question: 'Can you help with urgent or short-notice moves in Walsall?',
        answer: 'Yes, subject to availability. We can often help with urgent bookings where customers need a professional moving team quickly.',
      },
    ],
  },
  {
    path: '/areas-we-cover/cannock',
    navLabel: 'Cannock',
    cardTitle: 'Cannock Removals',
    title: 'Removals in Cannock by VIP House Movers',
    description: 'Book VIP House Movers for dependable removals in Cannock, with premium support for household moves, office relocations, and specialist transport.',
    intro: 'VIP House Movers helps customers in Cannock enjoy a smoother moving experience through careful planning, respectful handling, and clear communication from the first enquiry to the final delivery.',
    highlights: [
      'Professional house and flat removals across Cannock',
      'Commercial moving support for offices and workspaces',
      'Packing assistance and furniture handling available',
      'Reliable team focused on punctuality and customer care',
    ],
    movingSupport: [
      'Property-specific planning for houses, flats, and business addresses.',
      'Secure loading and transport for furniture, appliances, boxes, and specialist items.',
      'Flexible support for local moves and relocations to or from Cannock.',
      'A professional service that keeps disruption low and standards high.',
    ],
    idealFor: [
      'Families and individuals moving around Cannock',
      'Customers who want extra help with packing and setup',
      'Businesses seeking a dependable removals partner',
    ],
    seoPoints: [
      'Suitable for Cannock home removals, office moves, and packing support under one roof.',
      'Helpful for customers who want a smoother move with furniture handling included.',
      'Works well for local property changes and longer-distance relocations from Cannock.',
      'A strong choice for customers looking for dependable service and clear communication.',
    ],
    faqs: [
      {
        question: 'Can you help with both home and office removals in Cannock?',
        answer: 'Yes. VIP House Movers supports household and commercial removals in Cannock, with service tailored to the size and complexity of the move.',
      },
      {
        question: 'Do you offer packing services for Cannock customers?',
        answer: 'Yes. Packing assistance can be added if you want extra help protecting and preparing your belongings before moving day.',
      },
    ],
  },
  {
    path: '/areas-we-cover/dudley',
    navLabel: 'Dudley',
    cardTitle: 'Dudley Removals',
    title: 'Removals in Dudley by VIP House Movers',
    description: 'VIP House Movers provides premium removals in Dudley, helping households and businesses move with careful planning and professional support.',
    intro: 'Whether you are relocating a family home, moving out of a flat, or shifting business equipment, VIP House Movers offers the practical help and premium service needed to make your Dudley move feel organised.',
    highlights: [
      'Full and part-load removals across Dudley and surrounding areas',
      'Support for homes, offices, and local commercial premises',
      'Careful handling with optional packing and furniture assembly',
      'Friendly movers who prioritise communication and reliability',
    ],
    movingSupport: [
      'Detailed planning around access, timing, and the scale of your move.',
      'Protection and transport for furniture, fragile items, boxes, and business contents.',
      'Flexible labour and vehicle options based on your moving needs.',
      'A smooth, customer-focused service from collection to unloading.',
    ],
    idealFor: [
      'Families moving home in Dudley',
      'Tenants and landlords needing practical moving support',
      'Companies relocating office furniture and equipment',
    ],
    seoPoints: [
      'Designed for Dudley house removals, rental moves, and practical office relocations.',
      'A good fit for customers who want packing help, loading support, and safe furniture transport.',
      'Suitable for straightforward local moves as well as more complex relocation projects.',
      'Offers a reliable option for customers who value presentation, punctuality, and careful handling.',
    ],
    faqs: [
      {
        question: 'Do you carry out domestic removals throughout Dudley?',
        answer: 'Yes. VIP House Movers supports families, tenants, and homeowners moving around Dudley and the wider Black Country area.',
      },
      {
        question: 'Can Dudley customers book packing and furniture handling too?',
        answer: 'Yes. We can tailor your move with packing support and furniture assistance if you want a more complete service.',
      },
    ],
  },
  {
    path: '/areas-we-cover/west-bromwich',
    navLabel: 'West Bromwich',
    cardTitle: 'West Bromwich Removals',
    title: 'Removals in West Bromwich by VIP House Movers',
    description: 'VIP House Movers handles removals in West Bromwich with premium care, efficient transport, and dependable support for local and regional moves.',
    intro: 'West Bromwich moves can involve tight streets, urban access, and varied property types, so VIP House Movers delivers a structured moving service that keeps every stage of the process under control.',
    highlights: [
      'Reliable removals for houses, flats, and business premises in West Bromwich',
      'Support for packing, loading, transport, and room placement',
      'Professional care for bulky furniture and valuable items',
      'Flexible move planning built around your schedule',
    ],
    movingSupport: [
      'Tailored moving plans for local households and commercial relocations.',
      'Practical support for city-style access, parking limits, and timed moves.',
      'Careful transport for everything from essentials to specialist items.',
      'A professional removals experience that reflects the VIP House Movers brand.',
    ],
    idealFor: [
      'Homeowners and tenants moving around West Bromwich',
      'Customers with time-sensitive or access-restricted moves',
      'Businesses seeking a polished relocation service',
    ],
    seoPoints: [
      'Well suited to West Bromwich home moves where timing, access, and parking need careful planning.',
      'Useful for flats, houses, and urban properties with tighter loading conditions.',
      'Strong option for business relocations requiring a smarter and more organised service.',
      'Gives customers a dependable removals team for both local and regional moves.',
    ],
    faqs: [
      {
        question: 'Can you handle removals in built-up parts of West Bromwich?',
        answer: 'Yes. VIP House Movers plans around tighter streets, timed access, and parking challenges so the move can be managed more smoothly.',
      },
      {
        question: 'Do you support business relocations in West Bromwich?',
        answer: 'Yes. We help offices and local businesses relocate furniture, equipment, and boxed items with organised planning and careful transport.',
      },
    ],
  },
  {
    path: '/areas-we-cover/telford',
    navLabel: 'Telford',
    cardTitle: 'Telford Removals',
    title: 'Removals in Telford by VIP House Movers',
    description: 'Choose VIP House Movers for professional removals in Telford, with efficient planning, premium handling, and flexible support for every type of move.',
    intro: 'VIP House Movers helps customers in Telford move home or business premises with confidence, offering a dependable service that balances efficiency, protection, and first-class customer care.',
    highlights: [
      'House removals, flat moves, and office relocations in Telford',
      'Packing support and careful handling for furniture and fragile items',
      'Flexible moving options for local and longer-distance relocations',
      'Friendly, experienced movers focused on getting the details right',
    ],
    movingSupport: [
      'Move planning tailored to property size, timelines, and destination.',
      'Careful loading, transport, and unloading with attention to fragile belongings.',
      'Support for urgent bookings, planned moves, and staged relocations.',
      'A professional removals service designed to keep stress to a minimum.',
    ],
    idealFor: [
      'Families relocating within Telford or from nearby towns',
      'Customers needing packing and transport under one roof',
      'Businesses moving offices or equipment efficiently',
    ],
    seoPoints: [
      'Ideal for Telford house removals, family relocations, and planned business moves.',
      'Works well for customers wanting packing, transport, and furniture handling from one provider.',
      'Suitable for local moves, longer-distance relocations, and timed property changes.',
      'A smart choice for customers who want a calm, structured moving day.',
    ],
    faqs: [
      {
        question: 'Do you cover both local and longer-distance removals from Telford?',
        answer: 'Yes. VIP House Movers supports moves within Telford and relocations to destinations across the Midlands and wider UK.',
      },
      {
        question: 'Can I combine packing and removals for a Telford move?',
        answer: 'Yes. We can build the service around the level of support you need, including packing, loading, transport, and unloading.',
      },
    ],
  },
  {
    path: '/areas-we-cover/lichfield',
    navLabel: 'Lichfield',
    cardTitle: 'Lichfield Removals',
    title: 'Removals in Lichfield by VIP House Movers',
    description: 'VIP House Movers provides premium removals in Lichfield with dependable planning, careful handling, and tailored support for smooth relocations.',
    intro: 'From period homes and modern properties to offices and apartment moves, VIP House Movers brings a polished and organised approach to relocations in Lichfield and the surrounding area.',
    highlights: [
      'Professional moving services for homes, apartments, and businesses in Lichfield',
      'Support for packing, transport, and furniture setup',
      'Careful handling that protects valuable and delicate belongings',
      'A premium, customer-led moving experience from start to finish',
    ],
    movingSupport: [
      'Detailed move planning around access, property style, and timelines.',
      'Secure transport for household furniture, office items, and fragile contents.',
      'Flexible removal services for local moves and longer relocations.',
      'Consistent communication that helps your Lichfield move stay on track.',
    ],
    idealFor: [
      'Homeowners looking for a premium removals company in Lichfield',
      'Customers moving valuable furniture and fragile belongings',
      'Businesses that need an organised office move',
    ],
    seoPoints: [
      'A premium fit for Lichfield homes, apartments, offices, and higher-value property moves.',
      'Useful for customers who want stronger care for delicate belongings and larger furniture.',
      'Suitable for local Lichfield moves and regional relocations beyond the city.',
      'Designed to appeal to customers looking for a more polished and carefully managed service.',
    ],
    faqs: [
      {
        question: 'Is VIP House Movers suitable for premium home moves in Lichfield?',
        answer: 'Yes. Our service is designed for customers who want a polished removals experience with careful handling and a high standard of customer care.',
      },
      {
        question: 'Do you move fragile and valuable items in Lichfield?',
        answer: 'Yes. We can support moves involving delicate décor, larger furniture, and items that need a more careful approach in transit.',
      },
    ],
  },
  {
    path: '/areas-we-cover/tamworth',
    navLabel: 'Tamworth',
    cardTitle: 'Tamworth Removals',
    title: 'Removals in Tamworth by VIP House Movers',
    description: 'VIP House Movers offers professional removals in Tamworth, combining efficient transport, careful handling, and premium customer service.',
    intro: 'If you are moving within Tamworth or planning a relocation to another part of the UK, VIP House Movers provides the structure and support needed to keep your move smooth, organised, and well managed.',
    highlights: [
      'House and flat removals throughout Tamworth',
      'Commercial relocation support for offices and small businesses',
      'Packing services and furniture handling available',
      'Professional movers who prioritise punctuality and care',
    ],
    movingSupport: [
      'Tailored move planning based on property type, access, and moving dates.',
      'Safe loading and transport for household goods, office furniture, and delicate items.',
      'Support for local relocations and long-distance moves from Tamworth.',
      'Clear communication that keeps your move simple and stress-free.',
    ],
    idealFor: [
      'Families moving home around Tamworth',
      'Tenants and first-time buyers planning a local move',
      'Businesses wanting dependable commercial moving help',
    ],
    seoPoints: [
      'Ideal for Tamworth house removals, tenant moves, and first-home relocations.',
      'Useful for customers who want packing, loading, and transport support in one service.',
      'Suitable for office moves and small business relocations that need efficiency and care.',
      'A dependable local option for both short-distance and longer-distance removals.',
    ],
    faqs: [
      {
        question: 'Do you help first-time buyers move in Tamworth?',
        answer: 'Yes. VIP House Movers supports first-time buyers, tenants, and growing families who want a smoother and better organised move day.',
      },
      {
        question: 'Can Tamworth businesses use your relocation service?',
        answer: 'Yes. We help offices and local businesses move furniture, equipment, and boxed contents with careful planning and efficient transport.',
      },
    ],
  },
  {
    path: '/areas-we-cover/sutton-coldfield',
    navLabel: 'Sutton Coldfield',
    cardTitle: 'Sutton Coldfield Removals',
    title: 'Removals in Sutton Coldfield by VIP House Movers',
    description: 'VIP House Movers delivers premium removals in Sutton Coldfield with careful planning, polished service, and trusted support for homes and businesses.',
    intro: 'Sutton Coldfield customers often want a removals company that combines professionalism, presentation, and care, and VIP House Movers is built to deliver that higher standard across every stage of the move.',
    highlights: [
      'Premium house removals and apartment moves in Sutton Coldfield',
      'Business relocations with organised planning and careful transport',
      'Support for packing, furniture assembly, and room placement',
      'High service standards with attention to detail throughout',
    ],
    movingSupport: [
      'Move plans tailored to larger homes, apartments, and office environments.',
      'Secure transport and careful handling for valuable furniture and possessions.',
      'Flexible support for local moves and longer-distance relocations.',
      'A polished moving experience aligned with the VIP House Movers brand.',
    ],
    idealFor: [
      'Homeowners seeking a premium removals service in Sutton Coldfield',
      'Families with larger properties and high-value belongings',
      'Businesses wanting a smart, efficient office relocation',
    ],
    seoPoints: [
      'Designed for Sutton Coldfield customers wanting a higher-end removals experience.',
      'Ideal for larger homes, premium apartments, and moves involving valuable furniture.',
      'Useful for customers who want packing, room placement, and furniture support included.',
      'A strong option for businesses needing a polished relocation service with minimal disruption.',
    ],
    faqs: [
      {
        question: 'Is this service suitable for larger homes in Sutton Coldfield?',
        answer: 'Yes. VIP House Movers is well suited to larger family properties where careful planning, stronger logistics, and premium handling are especially important.',
      },
      {
        question: 'Can you help with premium office moves in Sutton Coldfield?',
        answer: 'Yes. We support organised commercial relocations for offices and businesses that want a professional, efficient moving team.',
      },
    ],
  },
  {
    path: '/areas-we-cover/kidderminster',
    navLabel: 'Kidderminster',
    cardTitle: 'Kidderminster Removals',
    title: 'Removals in Kidderminster by VIP House Movers',
    description: 'VIP House Movers provides reliable removals in Kidderminster with premium support for domestic moves, commercial relocations, and packing assistance.',
    intro: 'Whether you are moving around Kidderminster, heading into Worcestershire, or relocating further afield, VIP House Movers offers the practical support and premium care needed for a smoother move.',
    highlights: [
      'House removals, flat moves, and office relocations in Kidderminster',
      'Careful handling for furniture, boxes, fragile items, and equipment',
      'Packing support available for a more efficient moving day',
      'Friendly, professional movers with clear communication',
    ],
    movingSupport: [
      'Move planning built around your property, timings, and access requirements.',
      'Safe transport for everything from essentials to larger furniture pieces.',
      'Flexible labour and vehicle options depending on move size and distance.',
      'A dependable removals service designed to keep your day organised.',
    ],
    idealFor: [
      'Families and renters moving within Kidderminster',
      'Customers who need practical help with packing and loading',
      'Businesses seeking an efficient relocation partner',
    ],
    seoPoints: [
      'Great for Kidderminster home moves, rental relocations, and office transport needs.',
      'Useful for customers who want practical moving help with packing and loading included.',
      'Suitable for family homes, flats, and business premises across the local area.',
      'A dependable option for organised move days with careful furniture handling.',
    ],
    faqs: [
      {
        question: 'Can you help renters and families move in Kidderminster?',
        answer: 'Yes. VIP House Movers supports both rented property moves and larger family home relocations in and around Kidderminster.',
      },
      {
        question: 'Do you offer packing support for Kidderminster moves?',
        answer: 'Yes. Packing assistance can be included if you want extra help protecting and organising your belongings before the move.',
      },
    ],
  },
  {
    path: '/areas-we-cover/stourbridge',
    navLabel: 'Stourbridge',
    cardTitle: 'Stourbridge Removals',
    title: 'Removals in Stourbridge by VIP House Movers',
    description: 'VIP House Movers handles removals in Stourbridge with premium care, dependable transport, and flexible support for homes, flats, and commercial spaces.',
    intro: 'VIP House Movers helps customers in Stourbridge enjoy a well-organised moving experience by combining clear planning, careful handling, and a premium standard of service across every stage of the move.',
    highlights: [
      'Professional removals for houses, flats, and offices in Stourbridge',
      'Packing support and furniture handling available',
      'Careful transport for fragile items and larger household belongings',
      'Reliable booking options for planned and urgent moves',
    ],
    movingSupport: [
      'Tailored plans for domestic and commercial relocations in Stourbridge.',
      'Safe loading and unloading with a strong focus on protecting belongings.',
      'Flexible support for short-distance and longer-distance moves.',
      'A premium customer journey with consistent communication throughout.',
    ],
    idealFor: [
      'Homeowners moving around Stourbridge and the surrounding area',
      'Customers wanting a removal company that balances care and efficiency',
      'Businesses needing structured relocation support',
    ],
    seoPoints: [
      'Well suited to Stourbridge homes, flats, and office spaces needing organised removals support.',
      'Useful when you want a service that balances careful handling with efficient move-day pacing.',
      'Suitable for fragile items, larger furniture, and practical packing support.',
      'A strong option for local customers seeking dependable communication and cleaner logistics.',
    ],
    faqs: [
      {
        question: 'Do you carry out both home and office removals in Stourbridge?',
        answer: 'Yes. VIP House Movers supports domestic relocations, apartment moves, and commercial removals in Stourbridge and nearby areas.',
      },
      {
        question: 'Can I book packing help for a Stourbridge move?',
        answer: 'Yes. We can include packing services if you want more support preparing your move and protecting fragile belongings.',
      },
    ],
  },
  {
    path: '/areas-we-cover/rugeley',
    navLabel: 'Rugeley',
    cardTitle: 'Rugeley Removals',
    title: 'Removals in Rugeley by VIP House Movers',
    description: 'VIP House Movers offers professional removals in Rugeley with careful planning, premium service, and trusted support for every kind of move.',
    intro: 'From compact local moves to larger household relocations, VIP House Movers helps Rugeley customers move with less stress through reliable planning, respectful handling, and a polished service approach.',
    highlights: [
      'Reliable house removals and flat moves in Rugeley',
      'Commercial moving support for offices and local businesses',
      'Packing services and furniture support available',
      'Friendly team committed to professionalism and care',
    ],
    movingSupport: [
      'Move planning around your property, access, and moving timeline.',
      'Secure transport for furniture, boxed items, and delicate belongings.',
      'Flexible support for local moves and relocations beyond Rugeley.',
      'A premium removals experience designed to simplify moving day.',
    ],
    idealFor: [
      'Families and individuals relocating in Rugeley',
      'Customers wanting a dependable team for careful transport',
      'Businesses needing organised office moving support',
    ],
    seoPoints: [
      'Ideal for Rugeley homeowners, renters, and businesses wanting a more organised move.',
      'Suitable for local house removals as well as longer-distance moves from the area.',
      'Helpful for customers who value careful loading, transport, and room placement support.',
      'A dependable choice when punctuality and communication matter as much as price.',
    ],
    faqs: [
      {
        question: 'Do you cover local and regional removals from Rugeley?',
        answer: 'Yes. VIP House Movers supports moves within Rugeley and to destinations across the wider Midlands and UK, depending on your requirements.',
      },
      {
        question: 'Can Rugeley businesses use your office relocation support?',
        answer: 'Yes. We can help businesses relocate furniture, equipment, and boxed contents with organised planning and efficient transport.',
      },
    ],
  },
  {
    path: '/areas-we-cover/burntwood',
    navLabel: 'Burntwood',
    cardTitle: 'Burntwood Removals',
    title: 'Removals in Burntwood by VIP House Movers',
    description: 'VIP House Movers provides premium removals in Burntwood for homes, flats, offices, and specialist moving jobs that need dependable support.',
    intro: 'Moving in Burntwood is easier when you have a team that plans ahead, communicates clearly, and handles every item with care, which is exactly what VIP House Movers is known for delivering.',
    highlights: [
      'Professional removals across Burntwood and nearby areas',
      'House moves, office relocations, and packing support available',
      'Careful handling for fragile items, large furniture, and boxed belongings',
      'Flexible move planning built around your requirements',
    ],
    movingSupport: [
      'Tailored moving plans for homes, rental properties, and business spaces.',
      'Efficient loading, transport, and unloading with a premium level of care.',
      'Support for straightforward local moves and more complex relocations.',
      'A customer-first service focused on making your Burntwood move feel simpler.',
    ],
    idealFor: [
      'Families moving within Burntwood',
      'Customers with larger furniture or fragile household items',
      'Businesses seeking a dependable moving company',
    ],
    seoPoints: [
      'Suitable for Burntwood family homes, rental moves, and commercial relocation support.',
      'Useful for customers moving bulky furniture, delicate items, and full household contents.',
      'Works well for local moves where flexibility and careful planning are important.',
      'A strong option for customers wanting a professional moving team with clear communication.',
    ],
    faqs: [
      {
        question: 'Do you handle larger furniture moves in Burntwood?',
        answer: 'Yes. VIP House Movers regularly helps customers move larger household furniture, boxed contents, and delicate items with a careful and organised approach.',
      },
      {
        question: 'Can Burntwood customers book both packing and removals?',
        answer: 'Yes. We can tailor the service to include packing assistance as well as transport and unloading support.',
      },
    ],
  },
  {
    path: '/areas-we-cover/willenhall',
    navLabel: 'Willenhall',
    cardTitle: 'Willenhall Removals',
    title: 'Removals in Willenhall by VIP House Movers',
    description: 'VIP House Movers handles removals in Willenhall with premium customer care, organised transport, and flexible support for homes and businesses.',
    intro: 'Whether you are moving around Willenhall or relocating further afield, VIP House Movers provides the planning, lifting, and transport support needed to make moving day feel more manageable.',
    highlights: [
      'House removals, flat moves, and office support in Willenhall',
      'Packing options and careful handling for every kind of move',
      'Reliable service for local relocations and urgent bookings',
      'Professional movers focused on care, punctuality, and presentation',
    ],
    movingSupport: [
      'Property-based move planning to match your access and timing requirements.',
      'Safe loading and transport for furniture, appliances, boxes, and specialist items.',
      'Flexible support for local removals and moves outside Willenhall.',
      'A polished removals service delivered with clear communication throughout.',
    ],
    idealFor: [
      'Families and tenants moving around Willenhall',
      'Customers wanting packing and transport support together',
      'Businesses needing an efficient relocation solution',
    ],
    seoPoints: [
      'Designed for Willenhall customers who want local removals support with flexible service options.',
      'Suitable for home moves, flat relocations, and straightforward office transport.',
      'Helpful when you want packing, lifting, loading, and transport handled by one team.',
      'A dependable choice for organised move days and professional presentation.',
    ],
    faqs: [
      {
        question: 'Can you help with both domestic and business moves in Willenhall?',
        answer: 'Yes. VIP House Movers supports family home moves, flat relocations, and office removals in and around Willenhall.',
      },
      {
        question: 'Do you offer urgent move support in Willenhall?',
        answer: 'Yes, subject to availability. We can often help when customers need a reliable team quickly for a short-notice move.',
      },
    ],
  },
  {
    path: '/areas-we-cover/bloxwich',
    navLabel: 'Bloxwich',
    cardTitle: 'Bloxwich Removals',
    title: 'Removals in Bloxwich by VIP House Movers',
    description: 'VIP House Movers offers dependable removals in Bloxwich with careful planning, premium support, and efficient transport for homes and businesses.',
    intro: 'From family homes and rental properties to local offices and specialist item transport, VIP House Movers helps Bloxwich customers move with confidence through a service built around care and organisation.',
    highlights: [
      'Professional domestic and commercial removals in Bloxwich',
      'Support for packing, lifting, transport, and furniture placement',
      'Careful handling of bulky furniture and fragile belongings',
      'A premium service designed to reduce stress on moving day',
    ],
    movingSupport: [
      'Tailored planning based on property size, move date, and access needs.',
      'Secure loading and transport for boxes, appliances, furniture, and valuables.',
      'Flexible moving support for local jobs and relocations further afield.',
      'A professional removals experience with the service standards VIP House Movers is known for.',
    ],
    idealFor: [
      'Customers moving house within Bloxwich',
      'Tenants and landlords preparing for a property change',
      'Businesses needing a careful and efficient office move',
    ],
    seoPoints: [
      'Useful for Bloxwich property changes involving rented homes, family houses, and office spaces.',
      'Suitable for customers who need support with furniture lifting, safe transport, and unloading.',
      'A practical fit for both smaller moves and more involved home relocations.',
      'Helps reduce move-day stress with better planning and dependable communication.',
    ],
    faqs: [
      {
        question: 'Do you support rental moves and landlord property changes in Bloxwich?',
        answer: 'Yes. VIP House Movers can help tenants, landlords, and homeowners manage property changes with practical moving support and careful transport.',
      },
      {
        question: 'Can businesses in Bloxwich use your office removal service?',
        answer: 'Yes. We help local businesses move furniture, equipment, and boxed contents with an organised and efficient service.',
      },
    ],
  },
  {
    path: '/areas-we-cover/hednesford',
    navLabel: 'Hednesford',
    cardTitle: 'Hednesford Removals',
    title: 'Removals in Hednesford by VIP House Movers',
    description: 'VIP House Movers provides premium removals in Hednesford with dependable service, careful handling, and tailored support for every type of move.',
    intro: 'VIP House Movers supports Hednesford customers with a moving service that feels organised, professional, and genuinely helpful, whether you are relocating locally or moving to a new area altogether.',
    highlights: [
      'Reliable house removals and flat moves in Hednesford',
      'Office and business relocation support available',
      'Packing services and furniture handling tailored to your move',
      'Premium customer care from enquiry to unloading',
    ],
    movingSupport: [
      'Move planning based on access, property size, and your preferred schedule.',
      'Careful loading and transport for furniture, boxes, and fragile items.',
      'Flexible support for smaller local jobs and larger home relocations.',
      'A premium removals process designed to keep your Hednesford move running smoothly.',
    ],
    idealFor: [
      'Families planning a local home move in Hednesford',
      'Customers wanting help with packing and safe transport',
      'Businesses seeking an organised moving team',
    ],
    seoPoints: [
      'Well suited to Hednesford family home moves, rental relocations, and smaller office jobs.',
      'Useful for customers who want packing help as well as reliable transport and unloading.',
      'Suitable for local moves and broader relocations from Hednesford into the wider region.',
      'A dependable option for customers looking for a more professional and supportive service.',
    ],
    faqs: [
      {
        question: 'Can I book packing support for a Hednesford move?',
        answer: 'Yes. VIP House Movers can include packing support if you want extra help preparing and protecting your belongings before the move.',
      },
      {
        question: 'Do you help businesses move in Hednesford?',
        answer: 'Yes. We can support office and business relocations with organised planning, careful loading, and efficient transport.',
      },
    ],
  },
  {
    path: '/areas-we-cover/penkridge',
    navLabel: 'Penkridge',
    cardTitle: 'Penkridge Removals',
    title: 'Removals in Penkridge by VIP House Movers',
    description: 'VIP House Movers offers professional removals in Penkridge with premium care, flexible support, and dependable planning for smooth relocations.',
    intro: 'Whether you are moving from a village property, a family home, or a local business address, VIP House Movers helps Penkridge customers move with less hassle and more confidence.',
    highlights: [
      'House removals, flat moves, and office relocations in Penkridge',
      'Packing services and furniture support available',
      'Careful transport for fragile, valuable, and bulky items',
      'Friendly, experienced movers focused on a premium customer experience',
    ],
    movingSupport: [
      'Tailored moving plans built around property type, timing, and access.',
      'Safe loading and transport for boxes, furniture, and specialist belongings.',
      'Flexible options for local removals and moves to or from Penkridge.',
      'A dependable service that keeps moving day structured and stress-free.',
    ],
    idealFor: [
      'Families and homeowners moving in Penkridge',
      'Customers wanting careful handling for valuable possessions',
      'Businesses needing efficient relocation support',
    ],
    seoPoints: [
      'Ideal for Penkridge village moves, family home relocations, and business transport needs.',
      'Suitable for customers who want extra care for valuable furniture and fragile items.',
      'Helpful when you need a removals team that can balance efficiency with premium service.',
      'Works well for local property changes and longer-distance moves from Penkridge.',
    ],
    faqs: [
      {
        question: 'Do you cover village and rural-style moves around Penkridge?',
        answer: 'Yes. VIP House Movers supports moves in Penkridge and surrounding areas, with planning adapted to property type, access, and travel distance.',
      },
      {
        question: 'Can you move valuable and fragile items in Penkridge?',
        answer: 'Yes. We regularly help customers move furniture, fragile décor, and boxed valuables with careful handling and transport.',
      },
    ],
  },
  {
    path: '/areas-we-cover/brownhills',
    navLabel: 'Brownhills',
    cardTitle: 'Brownhills Removals',
    title: 'Removals in Brownhills by VIP House Movers',
    description: 'VIP House Movers delivers premium removals in Brownhills with careful planning, organised transport, and trusted support for homes and businesses.',
    intro: 'VIP House Movers helps customers in Brownhills move with confidence by offering dependable planning, respectful handling, and a polished standard of customer care at every stage of the move.',
    highlights: [
      'Professional removals for houses, flats, and offices in Brownhills',
      'Packing support and furniture handling available when needed',
      'Careful transport for bulky, fragile, and sentimental items',
      'Reliable moving support for local and regional relocations',
    ],
    movingSupport: [
      'Move planning tailored to your property, schedule, and destination.',
      'Secure loading, transport, and unloading with strong attention to detail.',
      'Flexible service levels for smaller moves and larger relocations.',
      'A premium removals service that keeps your Brownhills move organised and less stressful.',
    ],
    idealFor: [
      'Families and renters moving around Brownhills',
      'Customers who want a dependable and professional moving team',
      'Businesses looking for structured relocation support',
    ],
    seoPoints: [
      'Suitable for Brownhills house moves, rental relocations, and practical office transport.',
      'Useful for customers who want dependable planning and careful loading from a professional team.',
      'Works well for moves involving fragile items, bulky furniture, and full household contents.',
      'A strong choice for customers who want clear communication and fewer surprises on move day.',
    ],
    faqs: [
      {
        question: 'Do you help renters and homeowners move in Brownhills?',
        answer: 'Yes. VIP House Movers supports both renters and homeowners in Brownhills with moving plans tailored to the size and complexity of the relocation.',
      },
      {
        question: 'Can Brownhills businesses use your removals service too?',
        answer: 'Yes. We can assist businesses with organised office and equipment moves as part of our broader commercial relocation support.',
      },
    ],
  },
];

const existingPaths = new Set(manualAreaPageConfigs.map((c) => c.path));

const generatedAreaPageConfigs = generateAllAreaConfigs().filter(
  (c) => !existingPaths.has(c.path),
);

export const areaPageConfigs = [...manualAreaPageConfigs, ...generatedAreaPageConfigs];

export const areaPages = areaPageConfigs.map((config) => ({
  ...config,
  component: createAreaPage(config),
}));
